import { useMemo, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { ExternalLink, Unlink, X, Info, Maximize2, Minimize2 } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { picColor } from '@/lib/utils'
import type { Project } from '@/lib/types'

// Tata letak kanvas
const COL_W = 250
const ROW_H = 132        // beri ruang vertikal lebih (judul bisa multi-baris penuh)
const NODE_W = 210
const NODE_H = 78        // tinggi minimum node
const ANCHOR_Y = 30      // titik sambung kabel (relatif dari atas node) — tetap walau node tinggi
const BAND_GAP = 40
const PAD = 28

export function ProjectDiagram({ onlyLinked = false }: { onlyLinked?: boolean }) {
  const { projects, tasks, getEffectiveTaskStatus, updateProject } = useApp()

  // Set node yang ditampilkan — kalau filter aktif, hanya yang punya korelasi/hubungan
  const shown = useMemo(
    () => onlyLinked
      ? projects.filter((p) =>
          (p.dependsOn && projects.some((x) => x.id === p.dependsOn)) ||
          projects.some((x) => x.dependsOn === p.id))
      : projects,
    [projects, onlyLinked],
  )
  const [source, setSource] = useState<string | null>(null)        // node terpilih utk menghubungkan
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null) // kabel terpilih (key = successor id)
  const [err, setErr] = useState<string | null>(null)
  const [full, setFull] = useState(false)                          // mode layar penuh (fokus)

  // Escape utk keluar dari layar penuh
  useEffect(() => {
    if (!full) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFull(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [full])

  const pctOf = (id: string) => {
    const pt = tasks.filter((t) => t.projectId === id)
    const d = pt.filter((t) => getEffectiveTaskStatus(t.id) === 'done').length
    return pt.length > 0 ? Math.round((d / pt.length) * 100) : 0
  }

  // ── Auto-layout: rantai (komponen terhubung) jadi band horizontal, project tunggal jadi grid ──
  const layout = useMemo(() => {
    const parent = new Map<string, string>()
    const find = (x: string): string => { let r = x; while (parent.get(r) !== r) r = parent.get(r)!; return r }
    shown.forEach((p) => parent.set(p.id, p.id))
    shown.forEach((p) => {
      if (p.dependsOn && parent.has(p.dependsOn)) {
        const a = find(p.id), b = find(p.dependsOn)
        if (a !== b) parent.set(a, b)
      }
    })
    const comps = new Map<string, Project[]>()
    shown.forEach((p) => { const r = find(p.id); if (!comps.has(r)) comps.set(r, []); comps.get(r)!.push(p) })

    const depthOf = (p: Project) => {
      let d = 0, cur = p.dependsOn, seen = new Set([p.id])
      while (cur && !seen.has(cur)) { seen.add(cur); const par = projects.find((x) => x.id === cur); if (!par) break; d++; cur = par.dependsOn }
      return d
    }

    const pos = new Map<string, { x: number; y: number }>()
    const chains: Project[][] = []
    const singles: Project[] = []
    comps.forEach((m) => (m.length >= 2 ? chains.push(m) : singles.push(m[0])))
    chains.sort((a, b) => b.length - a.length)

    let top = PAD
    let maxX = 0
    for (const members of chains) {
      const byDepth = new Map<number, Project[]>()
      members.forEach((m) => { const d = depthOf(m); if (!byDepth.has(d)) byDepth.set(d, []); byDepth.get(d)!.push(m) })
      const maxStack = Math.max(...Array.from(byDepth.values(), (a) => a.length))
      byDepth.forEach((nodes, depth) => {
        nodes.forEach((m, i) => {
          const x = PAD + depth * COL_W
          const y = top + i * ROW_H + ((maxStack - nodes.length) * ROW_H) / 2
          pos.set(m.id, { x, y }); maxX = Math.max(maxX, x + NODE_W)
        })
      })
      top += maxStack * ROW_H + BAND_GAP
    }

    if (singles.length) {
      top += chains.length ? 8 : 0
      const cols = Math.max(1, Math.floor(Math.max(maxX, 4 * COL_W) / COL_W))
      singles.forEach((m, idx) => {
        const x = PAD + (idx % cols) * COL_W
        const y = top + Math.floor(idx / cols) * ROW_H
        pos.set(m.id, { x, y }); maxX = Math.max(maxX, x + NODE_W)
      })
      top += Math.ceil(singles.length / cols) * ROW_H
    }

    const edges = shown
      .filter((p) => p.dependsOn && pos.has(p.dependsOn) && pos.has(p.id))
      .map((p) => ({ key: p.id, from: p.dependsOn as string, to: p.id }))

    return { pos, edges, width: Math.max(maxX + PAD, 800), height: top + PAD, hasChains: chains.length > 0 }
  }, [shown, projects])

  const wouldCycle = (src: string, tgt: string) => {
    if (src === tgt) return true
    let cur: string | null = src, seen = new Set<string>()
    while (cur && !seen.has(cur)) { if (cur === tgt) return true; seen.add(cur); cur = projects.find((p) => p.id === cur)?.dependsOn ?? null }
    return false
  }

  const nameOf = (id: string) => projects.find((p) => p.id === id)?.name ?? '—'

  const onNodeClick = (id: string) => {
    setErr(null); setSelectedEdge(null)
    if (!source) { setSource(id); return }
    if (source === id) { setSource(null); return }
    if (wouldCycle(source, id)) { setErr('Tidak bisa — akan membentuk lingkaran (cycle).'); setSource(null); return }
    updateProject(id, { dependsOn: source }) // id dikerjakan SETELAH source
    setSource(null)
  }

  const body = (
    <>
      {/* Banner instruksi */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#F2F1EC] bg-[#FAFAF8] flex-shrink-0">
        <div className="flex-1 min-w-0">
          {err ? (
            <p className="text-[12px] font-body text-[#DC2626] flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> {err}</p>
          ) : source ? (
            <p className="text-[12px] font-body text-[#1A1916] flex items-center gap-1.5 flex-wrap">
              <span className="w-2 h-2 rounded-full bg-[#6366F1]" />
              Pilih project yang dikerjakan <b>setelah</b> “{nameOf(source)}”
              <button onClick={() => setSource(null)} className="ml-1 text-[#6366F1] hover:underline">Batal</button>
            </p>
          ) : (
            <p className="text-[11px] font-body text-[#8A8A85] flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5 text-[#C0BDB6] flex-shrink-0" />
              Klik satu project lalu project berikutnya untuk <b className="text-[#6B6860]">menghubungkan</b> (kabel = “dikerjakan setelah”). Klik kabel untuk <b className="text-[#6B6860]">melepas</b>.
            </p>
          )}
        </div>
        <button
          onClick={() => setFull((f) => !f)}
          title={full ? 'Keluar layar penuh (Esc)' : 'Fokus / layar penuh'}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-body font-medium text-[#6B6860] bg-white border border-[#E0DFD8] hover:border-[#1A1916] hover:text-[#1A1916] transition-colors flex-shrink-0"
        >
          {full ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{full ? 'Keluar' : 'Fokus'}</span>
        </button>
      </div>

      {/* Kanvas */}
      <div
        className={`relative overflow-auto ${full ? 'flex-1 min-h-0' : ''}`}
        style={{
          maxHeight: full ? undefined : '70vh',
          backgroundColor: '#FBFBF9',
          backgroundImage: 'radial-gradient(#E6E5DF 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
        onClick={() => { setSource(null); setSelectedEdge(null) }}
      >
        <div className="relative" style={{ width: layout.width, height: layout.height }}>
          {/* Kabel */}
          <svg className="absolute inset-0 pointer-events-none" width={layout.width} height={layout.height}>
            {layout.edges.map((e) => {
              const f = layout.pos.get(e.from)!, t = layout.pos.get(e.to)!
              const x1 = f.x + NODE_W, y1 = f.y + ANCHOR_Y, x2 = t.x, y2 = t.y + ANCHOR_Y
              const dx = Math.max(50, Math.abs(x2 - x1) / 2)
              const d = `M ${x1} ${y1} C ${x1 + dx} ${y1} ${x2 - dx} ${y2} ${x2} ${y2}`
              const sel = selectedEdge === e.key
              return (
                <g key={e.key}>
                  <path d={d} fill="none" stroke={sel ? '#DC2626' : '#C0BDB6'} strokeWidth={sel ? 3 : 2.5}
                    style={{ transition: 'stroke 0.15s' }} />
                  <circle cx={x2} cy={y2} r={4} fill={sel ? '#DC2626' : '#C0BDB6'} />
                  {/* hit area */}
                  <path d={d} fill="none" stroke="transparent" strokeWidth={18}
                    style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                    onClick={(ev) => { ev.stopPropagation(); setSelectedEdge(sel ? null : e.key); setSource(null) }} />
                </g>
              )
            })}
          </svg>

          {/* Tombol lepas pada kabel terpilih */}
          {layout.edges.filter((e) => selectedEdge === e.key).map((e) => {
            const f = layout.pos.get(e.from)!, t = layout.pos.get(e.to)!
            const mx = (f.x + NODE_W + t.x) / 2, my = (f.y + t.y) / 2 + ANCHOR_Y
            return (
              <button
                key={`del-${e.key}`}
                onClick={(ev) => { ev.stopPropagation(); updateProject(e.to, { dependsOn: null }); setSelectedEdge(null) }}
                style={{ left: mx, top: my }}
                className="absolute -translate-x-1/2 -translate-y-1/2 z-20 flex items-center gap-1 px-2 py-1 rounded-lg bg-[#DC2626] text-white text-[10px] font-body font-bold shadow-lg hover:bg-[#B91C1C] transition-colors"
              >
                <Unlink className="w-3 h-3" /> Lepas
              </button>
            )
          })}

          {/* Node project */}
          {shown.map((p) => {
            const pos = layout.pos.get(p.id)
            if (!pos) return null
            const pct = pctOf(p.id)
            const isSource = source === p.id
            return (
              <div key={p.id} className="absolute z-10" style={{ left: pos.x, top: pos.y, width: NODE_W }}>
                <div
                  onClick={(ev) => { ev.stopPropagation(); onNodeClick(p.id) }}
                  style={{ minHeight: NODE_H }}
                  className={`relative rounded-xl border bg-white transition-all cursor-pointer overflow-hidden ${
                    isSource ? 'ring-2 ring-[#6366F1] border-[#6366F1] shadow-md'
                    : source ? 'border-[#E8E7E3] hover:border-[#6366F1] hover:ring-2 hover:ring-[#6366F1]/30 shadow-sm'
                    : 'border-[#E8E7E3] hover:border-[#C8C7BF] hover:shadow-md shadow-sm'
                  }`}
                >
                  <div className="h-1" style={{ backgroundColor: p.color }} />
                  <div className="p-2.5">
                    <div className="flex items-start gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: p.color }} />
                      <p className="flex-1 text-[12px] font-body font-semibold text-[#1A1916] leading-snug break-words">{p.name}</p>
                      <Link to={`/projects/${p.id}`} onClick={(ev) => ev.stopPropagation()}
                        title="Buka project" className="text-[#C0BDB6] hover:text-[#6366F1] transition-colors flex-shrink-0">
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2">
                      <div className="flex-1 h-1 bg-[#F0EFE9] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: p.color }} />
                      </div>
                      <span className="text-[9px] font-display font-bold" style={{ color: p.color }}>{pct}%</span>
                      {p.pic && (
                        <span className="w-4 h-4 rounded-full text-[8px] text-white font-bold flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: picColor(p.pic) }} title={p.pic}>
                          {p.pic.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )

  if (full) {
    return createPortal(
      <div className="fixed inset-0 z-[70] bg-white flex flex-col animate-fade-in">{body}</div>,
      document.body,
    )
  }
  return (
    <div className="bg-white border border-[#E8E7E3] rounded-2xl overflow-hidden reveal reveal-d2">{body}</div>
  )
}
